/**
 * voicePanelHeight — persistence + bounds helpers for ResizableVoicePanel.
 *
 * Extracted from ResizableVoicePanel.tsx (2026-06-28) so the component file
 * only exports the component itself — react-refresh/only-export-components
 * flags a file that exports both a component and standalone functions. These
 * helpers carry no JSX, so they live here and are imported by both the
 * component and its test.
 *
 * Persistence key: `vibe-designer:voice-panel-height-px` (single global
 * setting per the user's "the voice chat area at the top" phrasing).
 * Bounds: [VOICE_MIN_HEIGHT_PX, 70% of viewport].
 */
export const VOICE_PANEL_HEIGHT_KEY = 'vibe-designer:voice-panel-height-px';
export const VOICE_MIN_HEIGHT_PX = 96;

export function maxHeightPx(): number {
  if (typeof window === 'undefined') return 600;
  return Math.max(VOICE_MIN_HEIGHT_PX, Math.round(window.innerHeight * 0.7));
}

export function readVoicePanelHeight(): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(VOICE_PANEL_HEIGHT_KEY);
    if (raw === null) return null;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < VOICE_MIN_HEIGHT_PX) return null;
    // Defend against stale localStorage from a larger prior viewport. A
    // value above the current 70% bound applied verbatim as inline
    // height was breaking the flex layout in our 2026-06-05 dallas live
    // repro. Mirrors the n < MIN guard above — both bounds → null.
    if (n > maxHeightPx()) return null;
    return n;
  } catch {
    return null;
  }
}

export function writeVoicePanelHeight(px: number | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (px === null) {
      window.localStorage.removeItem(VOICE_PANEL_HEIGHT_KEY);
    } else {
      window.localStorage.setItem(VOICE_PANEL_HEIGHT_KEY, String(Math.round(px)));
    }
  } catch {
    // Quota / privacy mode — degrade silently.
  }
}
